/**
 * useAccountDragAndDrop Hook
 * 
 * Manages drag and drop functionality for account ordering.
 * 
 * Note: This file must be .tsx (not .ts) because it contains JSX syntax.
 */

import React from "react";
import { useSensors, useSensor, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { doc, writeBatch } from "firebase/firestore";
import { db } from "../../../firebase";
import { Account } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

interface UseAccountDragAndDropParams {
  workspaceId: string | undefined;
  accounts: Account[];
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
  groupedActiveAccounts: Record<string, Account[]>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseAccountDragAndDropReturn {
  sensors: ReturnType<typeof useSensors>;
  handleAccountDragEnd: (event: DragEndEvent, groupName: string) => Promise<void>;
}

/**
 * Hook to manage drag and drop for account ordering
 */
export function useAccountDragAndDrop({
  workspaceId,
  accounts,
  setAccounts,
  groupedActiveAccounts,
  setSnackbarElement,
  setSnackbarOpen,
}: UseAccountDragAndDropParams): UseAccountDragAndDropReturn {
  // Drag and drop sensors for account ordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle account drag end for ordering
  const handleAccountDragEnd = async (event: DragEndEvent, groupName: string): Promise<void> => {
    // In demo mode, account order cannot be changed
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Account order cannot be changed. Please log in to reorder accounts." />
      );
      setSnackbarOpen(true);
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const groupAccounts = groupedActiveAccounts[groupName] || [];
    const oldIndex = groupAccounts.findIndex((acc) => acc.id === active.id);
    const newIndex = groupAccounts.findIndex((acc) => acc.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder accounts and update order field in Firestore
    const reorderedAccounts = arrayMove(groupAccounts, oldIndex, newIndex);
    
    // Update local state immediately for better UX
    const updatedAccounts = [...accounts];
    reorderedAccounts.forEach((reorderedAccount, index) => {
      const accountIndex = updatedAccounts.findIndex((acc) => acc.id === reorderedAccount.id);
      if (accountIndex !== -1) {
        (updatedAccounts[accountIndex] as any).order = index;
      }
    });
    setAccounts(updatedAccounts);
    
    try {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }
      
      const batch = writeBatch(db);
      reorderedAccounts.forEach((account, index) => {
        const accountRef = doc(db, "workspaces", workspaceId, "accounts", account.id);
        batch.update(accountRef, { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating account order:", error);
      // Revert on error
      setAccounts(accounts);
      alert("Failed to update account order");
    }
  };

  return {
    sensors,
    handleAccountDragEnd,
  };
}
